/**
 * @typedef {import('@/types/money').Money} Money
 * @typedef {import('@/types/product').Product} Product
 * @typedef {import('@/types/product').Variation} Variation
 */

/**
 * @param {Product} product
 * @returns {boolean}
 */
function validMemberPrice(product) {
  if (product?.hide_price) return false;
  if (product.field_titles?.length > 0 && !product.same_price) {
    return (
      Array.isArray(product.variations) &&
      product.variations.some((variation) => variation?.member_price?.cents > 0)
    );
  }
  return (product?.member_price?.cents ?? 0) > 0;
}

/**
 * @param {{ label: string } | undefined} priceMax
 * @param {string | undefined} priceRange
 * @returns {boolean}
 */
function hasVariationsPriceCrossed(priceMax, priceRange) {
  if (!priceMax?.label) return false;
  const priceArray = priceRange ? priceRange.split(' ~ ') : [];
  if (priceArray.length <= 1) return true;
  return priceArray.at(-1) !== priceMax.label;
}

/**
 * @param {Product} product
 * @returns {string | null}
 */
function getLowerProductPrice(product) {
  if (product.member_price && product.member_price.cents > 0) {
    return product.member_price.label;
  }
  return product.price_sale?.label ?? null;
}

/**
 * @param {Product} product
 * @returns {string | null}
 */
function getLowestMemberPriceLabel(product) {
  if (product.lowest_member_price) return product.lowest_member_price.label;
  return product.lowest_price?.label ?? null;
}

// follow logic from shop repo price.liquid
// https://bitbucket.org/starlinglabs/shop.shoplineapp.com/src/2deecdc11d20c812a5f0b26c184bc7ed746ac753/app/views/v2_themes/ultra_chic/snippets/price.liquid?at=master#lines-10
// backend variation function logic is noted on: https://shopline.atlassian.net/wiki/spaces/EN/pages/4668391429/Product+price.liquid
/**
 * @param {Object} params
 * @param {Product} params.product
 * @param {boolean} params.isLogin
 * @param {boolean} params.isPriceRangeEnabled
 * @param {boolean} params.hasMemberPrice
 * @returns {{ salePrice: string | null, regularPrice: string | null, showPriceCross: boolean }}
 */
export function getProductPriceLabel({
  product,
  isLogin,
  isPriceRangeEnabled,
  hasMemberPrice,
}) {
  if (product.hide_price) {
    return {
      salePrice: null,
      regularPrice: null,
      showPriceCross: false,
    };
  }

  let showPriceCross = false;

  const isPriceVaries =
    product.field_titles?.length > 0 &&
    !product.same_price &&
    product.variations?.length > 0;

  const isMemberPriceEnabled = validMemberPrice(product) && hasMemberPrice;

  const showMemberPrice = isLogin && isMemberPriceEnabled;

  const isOnSale = !!((product.price_sale?.cents ?? 0) > 0);

  const hasDifferentPrices = product.price_range !== product.price_max?.label;

  const hasDifferentMemberPrices =
    product.member_price_range !== product.member_price_max?.label;

  const hasPriceMaxCrossed = hasVariationsPriceCrossed(
    product.price_max,
    product.price_range,
  );

  const hasMemberPriceMaxCrossed = hasVariationsPriceCrossed(
    product.member_price_max,
    product.member_price_range,
  );

  let regularPrice = null;
  let salePrice = null;

  if (isPriceVaries) {
    if (isPriceRangeEnabled) {
      if (showMemberPrice) {
        if (hasDifferentMemberPrices && product.member_price_range) {
          salePrice = product.member_price_range;
        }
        if (hasMemberPriceMaxCrossed) {
          regularPrice = product.member_price_max?.label ?? null;
          showPriceCross = true;
        }
      } else {
        if (hasDifferentPrices && product.price_range) {
          salePrice = product.price_range;
        }
        if (hasPriceMaxCrossed) {
          regularPrice = product.price_max?.label ?? null;
          showPriceCross = true;
        }
      }
    } else {
      const memberPriceMin = getLowestMemberPriceLabel(product);
      if (showMemberPrice && memberPriceMin) {
        regularPrice = memberPriceMin;
      } else if (product.lowest_price) {
        regularPrice = product.lowest_price.label;
      }
    }
  } else {
    regularPrice = product.price?.label ?? null;

    if (showMemberPrice) {
      salePrice = getLowerProductPrice(product);
    } else if (isOnSale) {
      salePrice = product.price_sale?.label ?? null;
    }

    if (isOnSale || showMemberPrice) {
      showPriceCross = true;
    }
  }

  return {
    salePrice,
    regularPrice,
    showPriceCross,
  };
}
